import { bunja } from 'bunja';
import { atom } from 'jotai';

import { calcPositionMarginExcludingFee } from '@flipster/features/src/shared/calculator/adjust-margin';
import { positionBracketBunja } from '@flipster/features/src/shared/position-bracket/state';
import { tradeModeBunja } from '@flipster/features/src/trade-mode/state';
import { i18nBunja } from '@flipster/i18n';
import { marketsBunja } from '@flipster/r230522/original/market';
import { ordersBunja } from '@flipster/r230522/original/order';
import { openPositionsBunja } from '@flipster/r230522/original/position';
import { validateBracketPositionSize } from '@flipster/utils/calculator/bracket-formula';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber, numberFormatter } from '@flipster/utils/num';

import { addPositionModalBunja } from './state';

interface InputError {
  priority: 'warning' | 'error';
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const addPositionValidationBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);
  const {
    positionAtom,
    instrumentAtom,
    inputModeAtom,
    targetPriceAtom,
    inputValueAtom,
    tabAtom,
    marginAtom,
    availableFundsAtom,
    bboPriceAtom,
    existingLiquidationPriceAtom,
  } = bunja.use(addPositionModalBunja);
  const { openPositionsAtom } = bunja.use(openPositionsBunja);
  const { pendingPositionsAtom } = bunja.use(ordersBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { tradeModeAtom } = bunja.use(tradeModeBunja);
  const { positionBracketAtom, positionBracketMultiplierAtom, symbolLevelBracketParamsAtom } =
    bunja.use(positionBracketBunja);

  const t = getT();

  const maxAvailableMarginAtom = atom((get) => {
    const position = get(positionAtom);
    const instrument = get(instrumentAtom);
    const availableFunds = get(availableFundsAtom);
    const bboPrice = get(bboPriceAtom);
    const openPositions = get(openPositionsAtom);

    const buffer = instrument.defaultMaxSlippage ? Num(1).plus(instrument.defaultMaxSlippage) : '1.01';
    const symbolPositions = openPositions?.[position.symbol] ?? [];
    const existAmount = symbolPositions
      .reduce((acc, cur) => acc.plus(Num(cur.position).abs()), Num(0))
      .multipliedBy(bboPrice);

    const maxAvailableOrderAmount = BigNumber.max(
      BigNumber.min(
        Num(instrument.notionalMaxOrderAmount).dividedBy(position.initLeverage),
        Num(instrument.notionalMaxPosition).minus(existAmount.multipliedBy(buffer)).dividedBy(position.initLeverage),
      ),
      0,
    );

    return BigNumber.min(availableFunds, maxAvailableOrderAmount).toFixed(
      getQuoteCurrencyPrecision(instrument.quoteCurrency),
    );
  });

  const bracketValidationAtom = atom((get) => {
    const currentPosition = get(positionAtom);
    const margin = get(marginAtom);
    const instrument = get(instrumentAtom);
    const tradeMode = get(tradeModeAtom);

    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

    const pendingPositions = get(pendingPositionsAtom);
    const positionBracket = get(positionBracketAtom);
    const market = get(marketsAtom)?.[currentPosition.symbol];
    if (!positionBracket || !tradeMode) {
      return {
        isValid: false,
        maxAvailableMargin: Num(get(maxAvailableMarginAtom) || 0),
      };
    }

    const entryPrice = Num(currentPosition.avgEntryPrice || 0);
    const markPrice = Num(market?.markPrice || 0);

    const bracketCalcPrice = (() => {
      switch (currentPosition.marginType) {
        case 'Isolated':
          return entryPrice;
        case 'Cross':
        default:
          return markPrice;
      }
    })();

    const newMargin = Num(margin || 0);
    const newPositionSize = Num(newMargin).multipliedBy(currentPosition.initLeverage);

    const { existingPositionSize, existingMargin } = get(symbolLevelBracketParamsAtom);

    const currentPendingPositionSize = pendingPositions?.[currentPosition.symbol]?.reduce((acc, cur) => {
      if (cur.slot === currentPosition.slot) return acc.plus(Num(cur.leavesQty).multipliedBy(cur?.price || 0));
      return acc;
    }, Num(0));
    const currentPositionSize = Num(currentPosition.position)
      .abs()
      .multipliedBy(bracketCalcPrice)
      .plus(currentPendingPositionSize || 0);
    const currentMargin = Num(calcPositionMarginExcludingFee(currentPosition));

    const { isValid, maxAvailableMargin: maxBracketAvailableMargin } = validateBracketPositionSize({
      tradeMode,
      symbolLevelParams: {
        existingPositionSize,
        newPositionSize,
        existingMargin,
        newLeverage: currentPosition.initLeverage,
      },
      positionLevelParams: {
        currentPositionSize,
        newPositionSize,
        currentMargin,
        newMargin,
      },
      positionBracket,
      positionBracketMultiplier: get(positionBracketMultiplierAtom),
      options: {
        availableMargin: Num(get(maxAvailableMarginAtom)),
      },
    });

    const maxAvailableMargin = BigNumber.max(
      0,
      BigNumber.min(maxBracketAvailableMargin, Num(get(maxAvailableMarginAtom))),
    ).decimalPlaces(quoteCurrencyPrecision, BigNumber.ROUND_DOWN);

    return {
      isValid,
      maxAvailableMargin,
    };
  });

  const minOrderMarginAtom = atom((get) => {
    const position = get(positionAtom);
    const instrument = get(instrumentAtom);
    const inputMode = get(inputModeAtom);
    const targetPrice = get(targetPriceAtom);

    switch (inputMode) {
      case 'margin': {
        const minAmount = BigNumber.max(
          Num(instrument.notionalMinOrderAmount),
          Num(instrument.unitOrderQty)
            .multipliedBy(targetPrice)
            .toFixed(getQuoteCurrencyPrecision(instrument.quoteCurrency), BigNumber.ROUND_UP),
        );

        return minAmount
          .dividedBy(position.initLeverage)
          .toFixed(getQuoteCurrencyPrecision(instrument.quoteCurrency), BigNumber.ROUND_UP);
      }
      case 'positionSize': {
        const minAmount = BigNumber.max(
          Num(instrument.notionalMinOrderAmount),
          Num(instrument.unitOrderQty)
            .multipliedBy(targetPrice)
            .toFixed(getQuoteCurrencyPrecision(instrument.quoteCurrency), BigNumber.ROUND_UP),
        );

        return minAmount.toFixed(getQuoteCurrencyPrecision(instrument.quoteCurrency), BigNumber.ROUND_UP);
      }
      case 'qty': {
        if (!targetPrice || Num(targetPrice).isZero()) return '0';
        const minQty = BigNumber.max(
          Num(instrument.unitOrderQty),
          Num(instrument.notionalMinOrderAmount).dividedBy(targetPrice),
        );
        return minQty.toFixed(instrument.tradeQtyPrecision, BigNumber.ROUND_UP);
      }
    }
  });

  const maxOrderAtom = atom((get) => {
    const position = get(positionAtom);
    const inputMode = get(inputModeAtom);
    const instrument = get(instrumentAtom);
    const targetPrice = get(targetPriceAtom);

    const maxAmount = Num(instrument?.notionalMaxOrderAmount || Infinity);

    switch (inputMode) {
      case 'margin':
        return maxAmount.dividedBy(position.initLeverage);
      case 'positionSize':
        return maxAmount;
      case 'qty':
        if (!targetPrice || Num(targetPrice).isZero()) return Num(Infinity);
        return maxAmount.dividedBy(targetPrice);
      default:
        return Num(0);
    }
  });

  const marginInputErrorAtom = atom<InputError | null>((get) => {
    const position = get(positionAtom);
    const inputValue = get(inputValueAtom);
    const availableFunds = get(availableFundsAtom);
    const instrument = get(instrumentAtom);
    const minOrderMargin = get(minOrderMarginAtom);
    const targetPrice = get(targetPriceAtom);
    const maxOrder = get(maxOrderAtom);

    const inputMode = get(inputModeAtom);

    if (!inputValue) return null;

    switch (inputMode) {
      case 'margin': {
        if (Num(inputValue).gt(availableFunds)) {
          return {
            priority: 'error',
            message: t('Trade.r250401.Exceed_fund_error'),
          };
        }
        if (Num(inputValue).lt(minOrderMargin)) {
          return {
            priority: 'error',
            message: t('Trade.r250827.Min_margin_error', {
              amountQuoteCurrency: `${minOrderMargin} ${instrument.quoteCurrency}`,
            }),
          };
        }
        if (Num(inputValue).gt(maxOrder)) {
          return {
            priority: 'error',
            message: t('Trade.r250401.Exceed_position_size_adjust_margin_error'),
          };
        }
        break;
      }
      case 'positionSize': {
        if (Num(inputValue).dividedBy(position.initLeverage).gt(availableFunds)) {
          return {
            priority: 'error',
            message: t('Trade.r250401.Exceed_position_size_error'),
          };
        }
        if (Num(inputValue).lt(minOrderMargin)) {
          return {
            priority: 'error',
            message: t('Trade.r250827.Min_position_size_required_error', {
              amount: minOrderMargin,
              quoteCurrency: instrument.quoteCurrency,
            }),
          };
        }
        if (Num(inputValue).gt(maxOrder)) {
          return {
            priority: 'error',
            message: t('Trade.r250401.Exceed_position_size_adjust_value_error'),
          };
        }
        break;
      }
      case 'qty': {
        if (Num(inputValue).multipliedBy(targetPrice).dividedBy(position.initLeverage).gt(availableFunds)) {
          return {
            priority: 'error',
            message: t('Trade.r250401.Exceed_quantity_error'),
          };
        }
        if (Num(inputValue).lt(minOrderMargin)) {
          return {
            priority: 'error',
            message: t('Trade.r250401.Min_quantity_required_error', {
              symbol: instrument.positionUnit,
              amount: minOrderMargin,
            }),
          };
        }
        if (Num(inputValue).gt(maxOrder)) {
          return {
            priority: 'error',
            message: t('Trade.r250401.Exceed_quantity_adjust_error'),
          };
        }
        break;
      }
    }

    const { isValid, maxAvailableMargin } = get(bracketValidationAtom);
    const quoteCurrencyPrecision = getQuoteCurrencyPrecision(instrument.quoteCurrency);

    if (!isValid) {
      const message = maxAvailableMargin.gte(minOrderMargin)
        ? t('Trade.r251215.Can_add_margin_amount', {
            amount: numberFormatter(maxAvailableMargin, { precision: quoteCurrencyPrecision, toFixed: true }),
            symbol: instrument.quoteCurrency,
          })
        : t('Trade.r260107.Decrease_effective_leverage');
      return {
        priority: 'error',
        message,
        action: {
          label: t('Common.r250401.Learn_more'),
          onClick: () => {
            window.open(`/support/trading-rules/leverage-margin/${position.symbol}`, '_blank');
          },
        },
      };
    }

    return null;
  });

  const limitPriceInputErrorAtom = atom<InputError | null>((get) => {
    const position = get(positionAtom);
    const targetPrice = get(targetPriceAtom);
    const liquidationPrice = get(existingLiquidationPriceAtom);

    if (!liquidationPrice) return null;
    const isExceedLiquidationPrice =
      position.side === 'Long'
        ? Num(targetPrice).isLessThan(liquidationPrice)
        : Num(targetPrice).isGreaterThan(liquidationPrice);

    if (isExceedLiquidationPrice) {
      return {
        priority: 'warning',
        message: t('Trade.r251208.Exceed_liquidation_price_error'),
      };
    }

    return null;
  });

  const isErrorAtom = atom((get) => {
    const tab = get(tabAtom);
    const marginInputError = get(marginInputErrorAtom);
    const limitPriceInputError = get(limitPriceInputErrorAtom);

    switch (tab) {
      case 'market':
        return marginInputError !== null;
      case 'limit':
        return marginInputError !== null || limitPriceInputError !== null;
      default:
        return false;
    }
  });

  const isReviewDisabledAtom = atom((get) => {
    const inputValue = get(inputValueAtom);
    const isError = get(isErrorAtom);
    return !inputValue || isError;
  });

  return {
    maxAvailableMarginAtom,
    marginInputErrorAtom,
    limitPriceInputErrorAtom,
    isReviewDisabledAtom,
  };
});
addPositionValidationBunja.debugLabel = 'addPositionValidationBunja';
